// ── V153: 반제품 이름이 단가표 식재료와 겹칠 때 ───────────────────────────────
// 레시피·단가표는 '이름'으로 이어진다. 반제품 레시피 '나쵸치즈'(소스)가 식재료 '나쵸치즈'(사 온 치즈)를
// 재료로 쓰면 둘이 같은 이름이라 셈핏이 하나로 본다:
//   · 반제품을 저장할 때 식재료 줄이 반제품 원가로 덮였다 (V152까지의 syncSemiPrice) → 원가가 자기 자신을 재료로 계산
//   · 다른 레시피에서 반제품을 고를 수 없고 (단가표 줄이 하나뿐)
//   · 단가표에서 그 줄 이름을 바꾸면 반제품 이름도 같이 바뀌었다
// 막기: 반제품 이름은 단가표 식재료·다른 반제품과 겹치지 않게, 재료에 자기 이름을 넣지 않게 (RecipeEditor·PriceManager)
// 고치기: fixSemiClash — 반제품 이름을 바꾸고, 덮인 식재료 줄은 식재료로 되돌리고, 고른 레시피만 새 반제품으로
import { sameIngredientName } from './ingredientMatch';
import { SEMI_CATEGORY, syncSemiPrice } from './semiRename';

const nm = (x) => String((x && x.name) || '').trim();

/**
 * 반제품 r의 이름 겹침 — 없으면 null
 * @returns {{ selfRef, rawRows: [{p,i}], semiRows: [{p,i}], overwritten, usedIn: recipe[] } | null}
 *   overwritten: 식재료 줄이 반제품 원가로 덮여서 '반제품' 줄만 남은 경우 (원래 단가를 다시 넣어야 함)
 */
export function semiClashOf(r, allRecipes = [], prices = []) {
  if (!r || r.type !== 'semi' || !nm(r)) return null;
  const rows = (prices || []).map((p, i) => ({ p, i })).filter(({ p }) => p && sameIngredientName(p.name, r.name));
  const rawRows = rows.filter(({ p }) => p.category !== SEMI_CATEGORY);
  const semiRows = rows.filter(({ p }) => p.category === SEMI_CATEGORY);
  const selfRef = (r.ingredients || []).some(ing => sameIngredientName(ing && ing.name, r.name));
  if (!selfRef && !rawRows.length) return null;
  const usedIn = (allRecipes || []).filter(x => x && x.id !== r.id
    && (x.ingredients || []).some(ing => sameIngredientName(ing && ing.name, r.name)));
  return { selfRef, rawRows, semiRows, overwritten: selfRef && !rawRows.length && semiRows.length > 0, usedIn };
}

/** 새 반제품 이름 제안: '나쵸치즈(반제품)' — 겹치면 (반제품2)… */
export function suggestSemiName(name, allRecipes = [], prices = []) {
  const base = nm({ name });
  const taken = (n) => (allRecipes || []).some(r => sameIngredientName(r && r.name, n))
    || (prices || []).some(p => sameIngredientName(p && p.name, n));
  let cand = `${base}(반제품)`;
  for (let k = 2; taken(cand) && k < 50; k++) cand = `${base}(반제품${k})`;
  return cand;
}

/**
 * 반제품 이름을 정할 때의 문제 (없으면 '')
 *   · 단가표에 같은 이름의 식재료(반제품이 아닌 줄)
 *   · 같은 이름의 다른 반제품 레시피
 *   · 재료에 자기 이름
 * @returns {'raw'|'semi'|'self'|''}
 */
export function semiNameProblem(name, { recipeId = null, ingredients = [], allRecipes = [], prices = [] } = {}) {
  const n = String(name || '').trim();
  if (!n) return '';
  if ((prices || []).some(p => p && p.category !== SEMI_CATEGORY && sameIngredientName(p.name, n))) return 'raw';
  if ((allRecipes || []).some(r => r && r.id !== recipeId && r.type === 'semi' && sameIngredientName(r.name, n))) return 'semi';
  if ((ingredients || []).some(i => sameIngredientName(i && i.name, n))) return 'self';
  return '';
}

/**
 * 이름 겹침 고치기
 * @param {Array} categories
 * @param {Array} prices
 * @param {{ recipeId, newName, restore?: { category, price, net_g }, switchIds?: string[] }} opts
 *   restore: 덮인 식재료 줄을 되돌릴 값 (overwritten일 때)
 *   switchIds: 이 이름을 '반제품'으로 쓰던 다른 레시피 — 재료 이름을 새 반제품 이름으로
 * @returns {{ categories, prices, recipe } | null}
 */
export function fixSemiClash(categories, prices, { recipeId, newName, restore = null, switchIds = [] } = {}) {
  const all = (categories || []).flatMap(c => (c && c.items) || []);
  const r = all.find(x => x && x.id === recipeId);
  const n = String(newName || '').trim();
  if (!r || !n || sameIngredientName(n, r.name)) return null;
  if (all.some(x => x && x.id !== r.id && sameIngredientName(x.name, n))) return null;
  if ((prices || []).some(p => p && sameIngredientName(p.name, n))) return null;
  const old = r.name;
  const renamed = { ...r, name: n };
  delete renamed.name_zh;   // 옛 이름의 중국어 → 저장 뒤 다시 번역
  const sw = new Set(switchIds || []);
  const nextCats = (categories || []).map(c => ({
    ...c,
    items: ((c && c.items) || []).map(item => {
      if (!item) return item;
      if (item.id === r.id) return renamed;
      if (!sw.has(item.id)) return item;
      return { ...item, ingredients: (item.ingredients || []).map(ing => (sameIngredientName(ing && ing.name, old) ? { ...ing, name: n } : ing)) };
    }),
  }));
  // 단가표: 같은 이름 줄 정리 — 식재료 줄은 남기고, 반제품 줄은 (덮인 거면 식재료로 되돌리고) 나머지는 뺀다
  let ps = [...(prices || [])];
  const same = ps.map((p, i) => ({ p, i })).filter(({ p }) => p && sameIngredientName(p.name, old));
  const raw = same.filter(({ p }) => p.category !== SEMI_CATEGORY);
  const semi = same.filter(({ p }) => p.category === SEMI_CATEGORY);
  const drop = new Set();
  if (!raw.length && semi.length && restore) {
    const [first, ...rest] = semi;
    const price = restore.price === '' || restore.price == null ? '' : Number(restore.price);
    const back = { ...first.p, category: restore.category || '공산품', price: Number.isFinite(price) ? price : '', net_g: Number(restore.net_g) || 0 };
    delete back.loss_g;
    ps[first.i] = back;
    rest.forEach(({ i }) => drop.add(i));
  } else if (raw.length) {
    semi.forEach(({ i }) => drop.add(i));   // 이 반제품의 옛 줄 — 새 이름으로 다시 만든다
  }
  ps = ps.filter((_, i) => !drop.has(i));
  ps = syncSemiPrice(ps, renamed);
  return { categories: nextCats, prices: ps, recipe: renamed };
}

/** 한국어 조사 — josa('나쵸치즈', '이/가') → '나쵸치즈가', josa('마늘', '이/가') → '마늘이' (한글로 안 끝나면 '이(가)') */
export function josa(word, pair) {
  const w = String(word || '');
  const [withB, withoutB] = String(pair).split('/');
  const m = w.replace(/[\s)\]'"’”]+$/, '').slice(-1);
  const code = m ? m.charCodeAt(0) - 0xAC00 : -1;
  if (code < 0 || code > 11171) return `${w}${withB}(${withoutB})`;
  const batchim = code % 28;
  // '(으)로' 계열은 ㄹ 받침이면 받침 없는 쪽 — 여기서는 이/가 · 을/를 · 와/과 · 은/는만 쓴다
  return `${w}${batchim ? withB : withoutB}`;
}
